import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ChevronLeft, Upload, X, Check, Loader2, Globe, Calendar, Users, Shield, Calculator, ShoppingBag, Zap, Code, FileText } from "lucide-react";
import { STEPS, PROJECT_TYPES, INDUSTRIES, COMPANY_SIZES, BUDGETS, TIMELINES, DESIGN_STYLES, DYNAMIC_QUESTIONS } from "../data/onboardingData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/edcc2150-0b82-47f9-839c-552e0da40dc2/images/70bd0f022afb547c51af551a9eca883fb3912ac76a7a6f74fc7b6ed18d9448fe.png";

const iconMap = { globe: Globe, calendar: Calendar, users: Users, shield: Shield, calculator: Calculator, "shopping-bag": ShoppingBag, zap: Zap, code: Code };

function ProgressBar({ step, total }) {
  return (
    <div className="flex gap-1" data-testid="progress-bar">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-500 ${i <= step ? "bg-[#0A8294]" : "bg-[#E5E7EB]"}`} />
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <label className="font-mono text-[10px] tracking-[0.2em] text-[#64748B] block">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-white border border-[#E5E7EB] text-[#0A1628] px-4 py-3 rounded-sm text-sm focus:border-[#0A8294] focus:ring-1 focus:ring-[#0A8294]/30 outline-none transition-colors placeholder:text-[#94A3B8]";
const selectCls = inputCls;
const textareaCls = inputCls + " resize-none";

export default function OnboardingPage() {
  const [step, setStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Start form
  const [startForm, setStartForm] = useState({ email: "", company_name: "", contact_name: "" });

  // All answers
  const [answers, setAnswers] = useState({
    industry: "", company_size: "", current_website: "", business_description: "",
    project_types: [], goals: "", pain_points: "", current_solutions: "",
    design_style: "", colors: "", brand_tone: "", reference_sites: "",
    specific_features: "", integrations: "", technical_requirements: "",
    timeline: "", budget: "", priority: "",
  });

  const [files, setFiles] = useState([]);
  const [meeting, setMeeting] = useState({ preferred_dates: ["", "", ""], preferred_time: "morning", notes: "" });

  const updateAnswer = (key, value) => setAnswers(prev => ({ ...prev, [key]: value }));
  const toggleProjectType = (id) => {
    setAnswers(prev => ({
      ...prev,
      project_types: prev.project_types.includes(id)
        ? prev.project_types.filter(t => t !== id)
        : [...prev.project_types, id],
    }));
  };

  // Auto-save
  const saveProgress = useCallback(async () => {
    if (!sessionId) return;
    setSaving(true);
    try {
      await axios.put(`${API}/onboarding/${sessionId}`, { step, data: answers });
    } catch (e) { /* silent */ }
    setSaving(false);
  }, [sessionId, step, answers]);

  useEffect(() => {
    if (sessionId && step > 0) {
      const timer = setTimeout(saveProgress, 1500);
      return () => clearTimeout(timer);
    }
  }, [answers, step, saveProgress, sessionId]);

  const handleStart = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/onboarding`, startForm);
      setSessionId(data.id);
      if (data.answers && Object.keys(data.answers).length > 0) {
        setAnswers(prev => ({ ...prev, ...data.answers }));
      }
      if (data.files) setFiles(data.files.map(f => ({ ...f, existing: true })));
      setStep(data.current_step || 1);
    } catch (err) {
      alert("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !sessionId) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/onboarding/${sessionId}/files`, formData);
      setFiles(prev => [...prev, { ...data, existing: true }]);
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  const removeFile = async (fileId) => {
    if (!sessionId) return;
    try {
      await axios.delete(`${API}/onboarding/${sessionId}/files/${fileId}`);
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (e) { /* silent */ }
  };

  const handleSubmit = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      await axios.put(`${API}/onboarding/${sessionId}`, { data: answers });
      await axios.post(`${API}/onboarding/${sessionId}/book`, {
        preferred_dates: meeting.preferred_dates.filter(Boolean),
        preferred_time: meeting.preferred_time,
        notes: meeting.notes,
      });
      setSubmitted(true);
    } catch (err) {
      alert("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const next = () => { saveProgress(); setStep(s => Math.min(s + 1, STEPS.length - 1)); };
  const prev = () => setStep(s => Math.max(s - 1, 0));

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#0A8294]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-[#0A8294]" />
          </div>
          <h1 className="font-outfit font-bold text-3xl text-[#0A1628] mb-3">Thank you!</h1>
          <p className="text-[#475569] font-manrope mb-6">Your project brief has been submitted. We'll review it and get back to you within 24 hours to schedule your feasibility study.</p>
          <a href="/" data-testid="back-home-btn" className="text-sm text-[#0A8294] hover:underline">&larr; Back to wedowedo.com</a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="onboarding-page">
      {/* Header */}
      <div className="border-b border-[#F1F5F9] bg-[#FAFAF7]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="wedowedo" className="h-7 w-7" />
            <span className="font-outfit font-medium text-[#0A1628] text-base">wedowedo</span>
          </a>
          <div className="flex items-center gap-3">
            {saving && <span className="text-xs text-[#94A3B8] font-mono">Saving...</span>}
            {step > 0 && <span className="text-xs text-[#64748B] font-mono">{step}/{STEPS.length - 1}</span>}
          </div>
        </div>
        {step > 0 && <div className="max-w-3xl mx-auto px-6 pb-3"><ProgressBar step={step - 1} total={STEPS.length - 1} /></div>}
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>

            {/* Step 0: Start */}
            {step === 0 && (
              <div className="max-w-lg mx-auto">
                <span className="font-mono text-[10px] tracking-[0.25em] text-[#0A8294] block mb-4">CLIENT ONBOARDING</span>
                <h1 className="font-outfit font-bold text-4xl md:text-5xl text-[#0A1628] mb-4 leading-tight">Let's build something great together</h1>
                <p className="text-[#475569] font-manrope mb-10">This guided process helps us understand your business and project needs. It takes about 10 minutes and you can save progress at any time.</p>
                <form onSubmit={handleStart} className="space-y-5" data-testid="start-form">
                  <Field label="YOUR NAME"><input required value={startForm.contact_name} onChange={e => setStartForm(p => ({ ...p, contact_name: e.target.value }))} className={inputCls} placeholder="Full name" data-testid="start-name" /></Field>
                  <Field label="COMPANY"><input required value={startForm.company_name} onChange={e => setStartForm(p => ({ ...p, company_name: e.target.value }))} className={inputCls} placeholder="Company name" data-testid="start-company" /></Field>
                  <Field label="EMAIL"><input required type="email" value={startForm.email} onChange={e => setStartForm(p => ({ ...p, email: e.target.value }))} className={inputCls} placeholder="your@company.com" data-testid="start-email" /></Field>
                  <button type="submit" disabled={loading} data-testid="start-submit" className="w-full bg-[#0A8294] text-white font-medium py-3.5 rounded-sm hover:bg-[#086d7d] hover:shadow-[0_8px_24px_-8px_rgba(10,130,148,0.5)] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get Started <ChevronRight className="w-4 h-4" /></>}
                  </button>
                </form>
              </div>
            )}

            {/* Step 1: Business */}
            {step === 1 && (
              <div>
                <StepHeader title="About Your Business" desc="Help us understand your company and industry." />
                <div className="space-y-6">
                  <Field label="INDUSTRY">
                    <select value={answers.industry} onChange={e => updateAnswer("industry", e.target.value)} className={selectCls} data-testid="business-industry">
                      <option value="">Select industry...</option>
                      {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </Field>
                  <Field label="COMPANY SIZE">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {COMPANY_SIZES.map(s => (
                        <button key={s} type="button" onClick={() => updateAnswer("company_size", s)}
                          className={`px-4 py-3 rounded-sm text-sm border transition-all ${answers.company_size === s ? "border-[#0A8294] bg-[#0A8294]/5 text-[#0A8294]" : "border-[#E5E7EB] text-[#475569] hover:border-[#94A3B8]"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="CURRENT WEBSITE (if any)"><input value={answers.current_website} onChange={e => updateAnswer("current_website", e.target.value)} className={inputCls} placeholder="https://..." /></Field>
                  <Field label="DESCRIBE YOUR BUSINESS"><textarea value={answers.business_description} onChange={e => updateAnswer("business_description", e.target.value)} className={textareaCls} rows={4} placeholder="What does your company do? Who are your customers?" data-testid="business-description" /></Field>
                </div>
              </div>
            )}

            {/* Step 2: Project Type */}
            {step === 2 && (
              <div>
                <StepHeader title="What do you need?" desc="Select all that apply. We'll tailor the next questions based on your choices." />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="project-type-grid">
                  {PROJECT_TYPES.map(pt => {
                    const Icon = iconMap[pt.icon] || Code;
                    const selected = answers.project_types.includes(pt.id);
                    return (
                      <button key={pt.id} type="button" onClick={() => toggleProjectType(pt.id)} data-testid={`project-type-${pt.id}`}
                        className={`flex items-start gap-4 p-5 rounded-sm border text-left transition-all ${selected ? "border-[#0A8294] bg-[#0A8294]/5" : "border-[#E5E7EB] hover:border-[#94A3B8]"}`}>
                        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${selected ? "text-[#0A8294]" : "text-[#94A3B8]"}`} />
                        <div>
                          <span className={`text-sm font-medium block ${selected ? "text-[#0A1628]" : "text-neutral-300"}`}>{pt.label}</span>
                          <span className="text-xs text-[#94A3B8]">{pt.desc}</span>
                        </div>
                        {selected && <Check className="w-4 h-4 text-[#0A8294] ml-auto mt-0.5 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3: Goals */}
            {step === 3 && (
              <div>
                <StepHeader title="Goals & Challenges" desc="What are you trying to achieve and what problems are you facing?" />
                <div className="space-y-6">
                  <Field label="WHAT DO YOU WANT TO ACHIEVE?"><textarea value={answers.goals} onChange={e => updateAnswer("goals", e.target.value)} className={textareaCls} rows={4} placeholder="e.g. Increase leads, automate manual processes, improve customer experience..." data-testid="goals-input" /></Field>
                  <Field label="CURRENT PAIN POINTS"><textarea value={answers.pain_points} onChange={e => updateAnswer("pain_points", e.target.value)} className={textareaCls} rows={3} placeholder="What's not working today? What takes too much time?" /></Field>
                  <Field label="CURRENT TOOLS / SOLUTIONS"><textarea value={answers.current_solutions} onChange={e => updateAnswer("current_solutions", e.target.value)} className={textareaCls} rows={3} placeholder="What tools or systems do you use today?" /></Field>
                </div>
              </div>
            )}

            {/* Step 4: Dynamic questions based on project type */}
            {step === 4 && (
              <div>
                <StepHeader title="Project Details" desc="Specific questions based on your selected project types." />
                <div className="space-y-8">
                  {answers.project_types.length === 0 && <p className="text-[#64748B] text-sm">Go back and select at least one project type.</p>}
                  {answers.project_types.map(pt => {
                    const questions = DYNAMIC_QUESTIONS[pt] || [];
                    const ptLabel = PROJECT_TYPES.find(p => p.id === pt)?.label || pt;
                    return (
                      <div key={pt}>
                        <h3 className="font-outfit font-medium text-[#0A1628] text-lg mb-4 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-[#0A8294]" /> {ptLabel}
                        </h3>
                        <div className="space-y-5 ml-4">
                          {questions.map(q => (
                            <Field key={q.key} label={q.label}>
                              {q.type === "textarea" && <textarea value={answers[q.key] || ""} onChange={e => updateAnswer(q.key, e.target.value)} className={textareaCls} rows={3} placeholder={q.placeholder} />}
                              {q.type === "text" && <input value={answers[q.key] || ""} onChange={e => updateAnswer(q.key, e.target.value)} className={inputCls} placeholder={q.placeholder} />}
                              {q.type === "select" && (
                                <select value={answers[q.key] || ""} onChange={e => updateAnswer(q.key, e.target.value)} className={selectCls}>
                                  <option value="">Select...</option>
                                  {q.options.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              )}
                            </Field>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 5: Design */}
            {step === 5 && (
              <div>
                <StepHeader title="Design & Brand" desc="Tell us about your visual preferences and brand identity." />
                <div className="space-y-6">
                  <Field label="DESIGN STYLE">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {DESIGN_STYLES.map(s => (
                        <button key={s} type="button" onClick={() => updateAnswer("design_style", s)}
                          className={`px-4 py-3 rounded-sm text-sm border transition-all ${answers.design_style === s ? "border-[#0A8294] bg-[#0A8294]/5 text-[#0A8294]" : "border-[#E5E7EB] text-[#475569] hover:border-[#94A3B8]"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="BRAND COLORS (if any)"><input value={answers.colors} onChange={e => updateAnswer("colors", e.target.value)} className={inputCls} placeholder="e.g. #00F0FF, dark blue, white" /></Field>
                  <Field label="BRAND TONE"><input value={answers.brand_tone} onChange={e => updateAnswer("brand_tone", e.target.value)} className={inputCls} placeholder="e.g. professional, modern, friendly, technical" /></Field>
                  <Field label="REFERENCE WEBSITES"><textarea value={answers.reference_sites} onChange={e => updateAnswer("reference_sites", e.target.value)} className={textareaCls} rows={3} placeholder="Links to websites or designs you like" /></Field>
                </div>
              </div>
            )}

            {/* Step 6: Features */}
            {step === 6 && (
              <div>
                <StepHeader title="Features & Integrations" desc="What specific functionality do you need?" />
                <div className="space-y-6">
                  <Field label="SPECIFIC FEATURES"><textarea value={answers.specific_features} onChange={e => updateAnswer("specific_features", e.target.value)} className={textareaCls} rows={4} placeholder="List any specific features or functionality you need..." data-testid="features-input" /></Field>
                  <Field label="INTEGRATIONS"><textarea value={answers.integrations} onChange={e => updateAnswer("integrations", e.target.value)} className={textareaCls} rows={3} placeholder="e.g. Stripe payments, Google Calendar, email marketing, ERP..." /></Field>
                  <Field label="TECHNICAL REQUIREMENTS"><textarea value={answers.technical_requirements} onChange={e => updateAnswer("technical_requirements", e.target.value)} className={textareaCls} rows={3} placeholder="Any specific technical needs? Hosting, security, compliance..." /></Field>
                </div>
              </div>
            )}

            {/* Step 7: Timeline */}
            {step === 7 && (
              <div>
                <StepHeader title="Timeline & Budget" desc="Help us plan resources and prioritize." />
                <div className="space-y-6">
                  <Field label="TIMELINE">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {TIMELINES.map(t => (
                        <button key={t} type="button" onClick={() => updateAnswer("timeline", t)}
                          className={`px-4 py-3 rounded-sm text-sm border text-left transition-all ${answers.timeline === t ? "border-[#0A8294] bg-[#0A8294]/5 text-[#0A8294]" : "border-[#E5E7EB] text-[#475569] hover:border-[#94A3B8]"}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="BUDGET RANGE">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {BUDGETS.map(b => (
                        <button key={b} type="button" onClick={() => updateAnswer("budget", b)}
                          className={`px-4 py-3 rounded-sm text-sm border text-left transition-all ${answers.budget === b ? "border-[#0A8294] bg-[#0A8294]/5 text-[#0A8294]" : "border-[#E5E7EB] text-[#475569] hover:border-[#94A3B8]"}`}>
                          {b}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="TOP PRIORITY"><input value={answers.priority} onChange={e => updateAnswer("priority", e.target.value)} className={inputCls} placeholder="What's the most important thing for this project?" /></Field>
                </div>
              </div>
            )}

            {/* Step 8: Files */}
            {step === 8 && (
              <div>
                <StepHeader title="Upload Files" desc="Share logos, mockups, documents, or any references that help us understand your vision." />
                <div className="space-y-4">
                  <label className="flex items-center justify-center gap-3 p-8 border-2 border-dashed border-[#E5E7EB] rounded-sm cursor-pointer hover:border-[#0A8294]/30 transition-colors" data-testid="file-upload-area">
                    <Upload className="w-5 h-5 text-[#64748B]" />
                    <span className="text-sm text-[#475569]">Click to upload files (max 5MB each)</span>
                    <input type="file" className="hidden" onChange={handleFileUpload} />
                  </label>
                  {files.length > 0 && (
                    <div className="space-y-2">
                      {files.map(f => (
                        <div key={f.id} className="flex items-center justify-between bg-white border border-[#F1F5F9] rounded-sm p-3">
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-[#0A8294]" />
                            <div>
                              <span className="text-sm text-[#0A1628] block">{f.filename}</span>
                              <span className="text-xs text-[#94A3B8]">{(f.size / 1024).toFixed(1)} KB</span>
                            </div>
                          </div>
                          <button onClick={() => removeFile(f.id)} className="text-[#94A3B8] hover:text-red-400"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-[#94A3B8]">Accepted: Images, PDFs, documents, spreadsheets. Max 5MB per file.</p>
                </div>
              </div>
            )}

            {/* Step 9: Review & Book */}
            {step === 9 && (
              <div>
                <StepHeader title="Review & Book Meeting" desc="Review your answers and schedule a feasibility study." />
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="bg-white border border-[#F1F5F9] rounded-sm p-6 space-y-4">
                    <h3 className="font-outfit font-medium text-[#0A1628]">Project Summary</h3>
                    <SummaryRow label="Company" value={startForm.company_name} />
                    <SummaryRow label="Industry" value={answers.industry} />
                    <SummaryRow label="Project Types" value={answers.project_types.map(pt => PROJECT_TYPES.find(p => p.id === pt)?.label).join(", ")} />
                    <SummaryRow label="Goals" value={answers.goals} />
                    <SummaryRow label="Design Style" value={answers.design_style} />
                    <SummaryRow label="Timeline" value={answers.timeline} />
                    <SummaryRow label="Budget" value={answers.budget} />
                    <SummaryRow label="Files" value={files.length > 0 ? `${files.length} file(s) uploaded` : "None"} />
                  </div>

                  {/* Meeting booking */}
                  <div className="bg-white border border-[#F1F5F9] rounded-sm p-6 space-y-4">
                    <h3 className="font-outfit font-medium text-[#0A1628]">Book a Meeting</h3>
                    <p className="text-sm text-[#64748B]">Select up to 3 preferred dates for your feasibility study.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[0, 1, 2].map(i => (
                        <Field key={i} label={`OPTION ${i + 1}`}>
                          <input type="date" value={meeting.preferred_dates[i]} onChange={e => {
                            const d = [...meeting.preferred_dates]; d[i] = e.target.value; setMeeting(p => ({ ...p, preferred_dates: d }));
                          }} className={inputCls} data-testid={`meeting-date-${i}`} />
                        </Field>
                      ))}
                    </div>
                    <Field label="PREFERRED TIME">
                      <div className="flex gap-2">
                        {["morning", "afternoon", "evening"].map(t => (
                          <button key={t} type="button" onClick={() => setMeeting(p => ({ ...p, preferred_time: t }))}
                            className={`flex-1 px-4 py-3 rounded-sm text-sm border capitalize transition-all ${meeting.preferred_time === t ? "border-[#0A8294] bg-[#0A8294]/5 text-[#0A8294]" : "border-[#E5E7EB] text-[#475569] hover:border-[#94A3B8]"}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                    </Field>
                    <Field label="ANYTHING ELSE?"><textarea value={meeting.notes} onChange={e => setMeeting(p => ({ ...p, notes: e.target.value }))} className={textareaCls} rows={2} placeholder="Any additional notes for the meeting..." /></Field>
                  </div>

                  <button onClick={handleSubmit} disabled={loading} data-testid="submit-onboarding"
                    className="w-full bg-[#0A8294] text-white font-medium py-4 rounded-sm hover:bg-[#086d7d] hover:shadow-[0_12px_32px_-8px_rgba(10,130,148,0.5)] transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-base">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5" /> Submit & Book Meeting</>}
                  </button>
                </div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        {step > 0 && step < STEPS.length && !submitted && (
          <div className="flex items-center justify-between mt-12 pt-8 border-t border-[#F1F5F9]">
            <button onClick={prev} data-testid="nav-prev" className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0A1628] transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {step < STEPS.length - 1 && (
              <button onClick={next} data-testid="nav-next" className="flex items-center gap-2 bg-white border border-[#E5E7EB] text-[#0A1628] px-6 py-2.5 rounded-sm text-sm hover:bg-[#F1F5F9] transition-all">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-8">
        <p className="text-xs text-neutral-700 font-mono">WeDoWeDo OÜ &middot; Tallinn, Estonia</p>
      </div>
    </div>
  );
}

function StepHeader({ title, desc }) {
  return (
    <div className="mb-8">
      <h2 className="font-outfit font-bold text-2xl md:text-3xl text-[#0A1628] mb-2">{title}</h2>
      <p className="text-[#64748B] font-manrope">{desc}</p>
    </div>
  );
}

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-start py-2 border-b border-[#F1F5F9] last:border-0">
      <span className="text-xs text-[#64748B] font-mono uppercase">{label}</span>
      <span className="text-sm text-[#0A1628] text-right max-w-[60%]">{value}</span>
    </div>
  );
}
